import { DataTable } from "@/components/table/table";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useGovernmentDocumentsModal } from "@/hooks/useGovernmentDocumentsModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { ConfirmModal } from "../ui/confirm-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { GovernmentDocumentsModalForm } from "./government-documents-modal-form";
import { columns } from "./government-documents.columns";

const documentTypes = [
  { value: "rfq", label: "RFQs & Bids" },
  { value: "audit", label: "Audits" },
  { value: "budget", label: "Budgets" },
  { value: "code", label: "City Codes" },
  { value: "resolution", label: "Resolutions" },
] as const;

type DocumentType = (typeof documentTypes)[number]["value"];

const documentFormSchema = z.object({
  documentType: z.enum(["rfq", "audit", "budget", "code", "resolution"]),
  number: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  date: z.date(),
  status: z
    .enum(["current", "archived", "proposed", "open", "closed", "awarded"])
    .default("current"),
  category: z.string().optional(),
  documentMediaId: z.string().optional(),
  // RFQ fields
  deadlineDate: z.date().optional(),
  awardedTo: z.string().optional(),
  estimatedValue: z.string().optional(),
  // Budget/Audit fields
  fiscalYear: z.number().optional(),
  // Code fields
  codeSection: z.string().optional(),
  effectiveDate: z.date().optional(),
});

type DocumentFormValues = z.infer<typeof documentFormSchema>;

export function GovernmentDocumentsTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const activeTab = useGovernmentDocumentsModal((s) => s.activeTab);
  const setActiveTab = useGovernmentDocumentsModal((s) => s.setActiveTab);

  const { data: allDocuments = [], isLoading } = useQuery(
    trpc.documents.list.queryOptions(),
  );

  // Filter documents by active tab
  const filteredDocuments = allDocuments.filter(
    (doc) => doc.documentType === activeTab,
  );

  const deleteDocumentId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteDocumentId = useDeleteEvent((s) => s.setToDeleteId);

  const isDocumentModalOpen = useGovernmentDocumentsModal((s) => s.isOpen);
  const setIsDocumentModalOpen = useGovernmentDocumentsModal(
    (s) => s.setIsModalOpen,
  );
  const setDocumentId = useGovernmentDocumentsModal((s) => s.setDocumentId);
  const documentId = useGovernmentDocumentsModal((s) => s.documentId);
  const defaultValues = useGovernmentDocumentsModal((s) => s.defaultValues);
  const setDefaultValues = useGovernmentDocumentsModal(
    (s) => s.setDefaultValues,
  );

  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      documentType: activeTab,
      number: "",
      title: "",
      description: "",
      date: new Date(),
      status: "current",
      category: "",
      documentMediaId: undefined,
      deadlineDate: undefined,
      awardedTo: "",
      estimatedValue: "",
      fiscalYear: new Date().getFullYear(),
      codeSection: "",
      effectiveDate: undefined,
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        documentType: defaultValues.documentType || activeTab,
        number: defaultValues.number || "",
        title: defaultValues.title || "",
        description: defaultValues.description || "",
        date: defaultValues.date || new Date(),
        status: defaultValues.status || "current",
        category: defaultValues.category || "",
        documentMediaId: defaultValues.documentMediaId,
        deadlineDate: defaultValues.deadlineDate,
        awardedTo: defaultValues.awardedTo || "",
        estimatedValue: defaultValues.estimatedValue || "",
        fiscalYear: defaultValues.fiscalYear || new Date().getFullYear(),
        codeSection: defaultValues.codeSection || "",
        effectiveDate: defaultValues.effectiveDate,
      });
    }
  }, [form, defaultValues, activeTab]);

  const deleteMutation = useMutation(
    trpc.documents.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        setDeleteDocumentId(null);
        toast.success("Document deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to delete document");
      },
    }),
  );

  const createMutation = useMutation(
    trpc.documents.create.mutationOptions({
      onSuccess: () => {
        form.reset();
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        setDocumentId(null);
        toast.success("Document created successfully");
        setIsDocumentModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create document");
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.documents.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        setDocumentId(null);
        setIsDocumentModalOpen(false);
        form.reset();
        toast.success("Document updated successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update document");
      },
    }),
  );

  const handleSubmit = (data: DocumentFormValues) => {
    if (documentId) {
      updateMutation.mutate({ ...data, id: documentId });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleNewDocument = () => {
    setDefaultValues({});
    form.reset({
      documentType: activeTab,
      number: "",
      title: "",
      description: "",
      date: new Date(),
      status: activeTab === "rfq" ? "open" : "current",
      category: "",
      documentMediaId: undefined,
      deadlineDate: undefined,
      awardedTo: "",
      estimatedValue: "",
      fiscalYear: new Date().getFullYear(),
      codeSection: "",
      effectiveDate: undefined,
    });
    setIsDocumentModalOpen(true);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as DocumentType);
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Government Documents
            </h2>
            <p className="text-muted-foreground">
              Manage RFQs, audits, budgets, codes, and resolutions
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={handleNewDocument}>New Document</Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            {documentTypes.map((type) => (
              <TabsTrigger key={type.value} value={type.value}>
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {documentTypes.map((type) => (
            <TabsContent key={type.value} value={type.value}>
              {!isLoading && (
                <DataTable
                  data={filteredDocuments}
                  columns={columns}
                  filterColumn="title"
                />
              )}
            </TabsContent>
          ))}
        </Tabs>

        <GovernmentDocumentsModalForm
          isModalOpen={isDocumentModalOpen}
          setIsModalOpen={setIsDocumentModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
          activeTab={activeTab}
        />

        <ConfirmModal
          open={Boolean(deleteDocumentId)}
          onConfirm={() => {
            if (deleteDocumentId)
              deleteMutation.mutate({ id: deleteDocumentId });
          }}
          onDismiss={() => {
            setDeleteDocumentId(null);
          }}
          isLoading={deleteMutation.isPending}
        />
      </div>
    </div>
  );
}
